// 影絵 (kagee) look: the world as layered black paper cut-outs, and colour
// carried only by light — lanterns, plankton, the moon, and translucent
// cellophane parts of the characters. Rendered live in 3D: the shelf is real
// geometry shaded into stepped paper tones, the pools are light boxes, and the
// characters are 3D models whose bodies are silhouettes and whose shells glow.
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CRAB_TINTS } from './water.js';
import { SHELLS, DEFAULT_SHELLS, SHELL_HOLES as HOLES } from '../sim/shells.js';
const SHELL_GLASS = Object.fromEntries(Object.entries(SHELLS).map(([id, s]) => [id, s.glass]));

export const KAGEE = {
  paper: [new THREE.Color('#030207'), new THREE.Color('#0c0e1f'), new THREE.Color('#171c3a')], // black paper (only [0] is used now)
  haze: new THREE.Color('#13265e'),        // distant layers fade toward the backlit sky
  lantern: new THREE.Color('#ffb04a'),
  rimLight: new THREE.Color('#9cc8ff'),
  skyTop: new THREE.Color('#03071a'), skyHorizon: new THREE.Color('#172a5e'),
};

// ------------------------------------------------------------------ environment

const envVS = /* glsl */`
attribute vec3 color;
varying vec3 vWorld; varying vec3 vNormal; varying float vAo;
uniform float hasColor;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vAo = hasColor > 0.5 ? color.r : 1.0;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const envFS = /* glsl */`
uniform vec3 moonDir; uniform vec3 paperLo; uniform vec3 paperMid; uniform vec3 paperHi;
uniform vec3 haze; uniform vec3 camPos; uniform vec4 lanterns[8]; uniform vec3 lanternColor;
uniform vec3 rimLight; uniform float poolGlow; uniform vec3 shelfTone;
varying vec3 vWorld; varying vec3 vNormal; varying float vAo;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main() {
  // Shadow-play: everything that is not a light is a black paper cut-out. No
  // shading, no rim light; the shapes are told only by their outline against
  // the lit water, the sea and the sky.
  vec3 c = paperLo;
  // A faint washi tooth so large black areas are not dead flat.
  vec2 fp = floor(vWorld.xz * 40.0);
  c *= 0.9 + 0.2 * hash(fp) * hash(fp + 17.0);
  // Distant sheets sit closer to the backlit screen: the multiplane depth of a shadow-play stage.
  float dist = length(vWorld - camPos);
  c = mix(c, haze, smoothstep(55.0, 230.0, dist) * 0.8);
  gl_FragColor = vec4(c, 1.0);
}`;

export function envMaterial(stage, hasColor) {
  return new THREE.ShaderMaterial({
    vertexShader: envVS, fragmentShader: envFS,
    uniforms: {
      moonDir: { value: stage.moonDir }, paperLo: { value: KAGEE.paper[0] }, paperMid: { value: KAGEE.paper[1] }, paperHi: { value: KAGEE.paper[2] },
      haze: { value: KAGEE.haze }, camPos: { value: stage.camera.position }, lanterns: { value: stage.kageeLanterns },
      lanternColor: { value: KAGEE.lantern }, rimLight: { value: KAGEE.rimLight }, poolGlow: { value: 0.22 }, shelfTone: { value: new THREE.Color('#081030') }, hasColor: { value: hasColor ? 1 : 0 },
    },
  });
}

// Lit windows and lanterns keep their glow as warm holes in the paper.
function glowMaterial(orig) {
  const c = (orig.emissive && orig.emissive.getHex() ? orig.emissive.clone() : orig.color.clone()).multiplyScalar(0.55 * Math.min(2, orig.emissiveIntensity || 1));
  // Warm paper light, never white: pull toward lamp amber and cap it so the
  // bloom gives a soft halo instead of a white block.
  const peak = Math.max(c.r, c.g, c.b);
  c.lerp(new THREE.Color(1.0, 0.55, 0.22).multiplyScalar(peak), 0.8);
  const top = Math.max(c.r, c.g, c.b);
  if (top > 0.55) c.multiplyScalar(0.55 / top);   // the post stack lifts this about 3x
  return new THREE.MeshBasicMaterial({ color: c });
}

export function applyKageeWorld(stage) {
  stage.kageeLanterns = Array.from({ length: 8 }, () => new THREE.Vector4(0, 0, 0, 0));
  const shared = { plain: envMaterial(stage, false), ao: envMaterial(stage, true) };
  stage.world.traverse(o => {
    if (!o.isMesh) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    const glowing = mats.some(m => (m.emissive && m.emissive.getHex() > 0) || /Lamp|Shoji|Window/i.test(m.name));
    o.material = glowing ? glowMaterial(mats[0]) : (o.geometry.attributes.color ? shared.ao : shared.plain);
    o.castShadow = false; o.receiveShadow = false;
  });
  stage.kageeEnv = shared;
  stage.moon.castShadow = false;
  // Backlit sky and sea: the light the silhouettes stand against.
  const u = stage.sky.material.uniforms;
  u.top.value.copy(KAGEE.skyTop); u.horizon.value.copy(KAGEE.skyHorizon);
  stage.sea.material.uniforms.kagee.value = 1;
  stage.scene.fog = null;
  stage.look.bloom = 0.8; stage.look.threshold = 0.7; stage.look.exposure = 1.05;
  stage.post.finalMat.uniforms.shadowTint.value.setRGB(0.9, 0.92, 1.1);
  stage.post.finalMat.uniforms.highTint.value.setRGB(1.05, 1.0, 0.94);
  stage.post.finalMat.uniforms.saturation.value = 1.18;
  stage.post.finalMat.uniforms.paper.value = 1;
}

// Called each frame: the nearest lanterns feed the paper shader.
export function updateKageeLanterns(stage) {
  if (!stage.kageeLanterns) return;
  const t = stage.rig.target;
  const near = [...stage.lights].sort((a, b) => a.pos.distanceToSquared(t) - b.pos.distanceToSquared(t)).slice(0, 8);
  stage.kageeLanterns.forEach((v, i) => {
    const l = near[i];
    if (l) v.set(l.pos.x, l.pos.y, l.pos.z, l.distance * (0.95 + 0.05 * Math.sin(stage.time * 6 + i)));
    else v.set(0, 0, 0, 0);
  });
}

// ------------------------------------------------------------------ characters

const bodyFS = /* glsl */`
uniform vec3 rim; uniform float opacity; uniform float dim; uniform vec3 glow;
varying vec3 vNormal; varying vec3 vView;
void main() {
  if (opacity < 0.999 && fract(sin(dot(floor(gl_FragCoord.xy), vec2(12.9898, 78.233))) * 43758.5453) > opacity) discard;
  float f = pow(1.0 - abs(dot(normalize(vNormal), normalize(vView))), 3.0);
  vec3 c = vec3(0.012, 0.010, 0.02) + rim * f * 0.3 * (1.0 - dim) + glow * 0.4;   // a lit paper edge so the silhouette reads on the dark screen
  gl_FragColor = vec4(c, 1.0);
}`;
const glassFS = /* glsl */`
uniform vec3 tint; uniform float opacity; uniform float dim; uniform vec3 glow; uniform float flash; uniform float time; uniform float pattern;
uniform float holes;
varying vec3 vNormal; varying vec3 vView; varying vec3 vWorld; varying vec3 vLocal;
float hash(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
// Holes cut in a black paper shell, one motif per shell kind; 1 = open.
float aa(float d) { return 1.0 - smoothstep(-fwidth(d), fwidth(d), d); }
float cutHoles(vec3 p, float kind) {
  if (kind < 0.5) { vec3 q = fract(p * 5.0) - 0.5; return aa(length(q.xy) - 0.2); }                          // round holes
  if (kind < 1.5) { return aa(abs(fract(p.y * 5.0 + p.x * 1.5) - 0.5) - 0.13); }                              // spiral slots
  if (kind < 2.5) { vec3 c = floor(p * 4.0); vec3 q = fract(p * 4.0) - 0.5 - (vec3(hash(c), hash(c + 3.1), 0.0) - 0.5) * 0.4;
                    return aa(length(q.xy) - 0.1 - 0.12 * hash(c + 7.7)) * step(0.35, hash(c + 1.3)); }       // cowrie spots
  if (kind < 3.5) { float a = atan(p.x, p.z) / 6.2832, r = length(p.xz) * 4.0 + p.y * 2.0;
                    return aa(abs(fract(r - a) - 0.5) - 0.11); }                                               // one spiral line
  float a = atan(p.x, p.y) / 6.2832; return aa(abs(fract(a * 14.0) - 0.5) - 0.16) * step(0.12, length(p.xy)); // radial ribs
}
void main() {
  if (opacity < 0.999 && fract(sin(dot(floor(gl_FragCoord.xy), vec2(12.9898, 78.233))) * 43758.5453) > opacity) discard;
  float f = 1.0 - abs(dot(normalize(vNormal), normalize(vView)));
  if (holes >= 0.0) {
    // A shadow puppet: black card, its colour only where the light comes through.
    float open = cutHoles(vLocal, holes);
    vec3 lit = tint * (1.5 + 0.3 * hash(floor(vLocal * 30.0)));
    if (pattern > 2.5) lit += vec3(1.0, 0.8, 0.3) * step(0.9, hash(floor(vLocal * 40.0)));
    vec3 c = mix(vec3(0.012, 0.01, 0.02), lit, open * (1.0 - dim * 0.8));
    c += tint * pow(f, 4.0) * 0.25 * (1.0 - dim);
    gl_FragColor = vec4(c + glow * (0.3 + open) + flash * open, 1.0);
    return;
  }
  // Cellophane: brighter where the sheet is thin (edges) and lower down, nearer the light box.
  float mottled = 0.85 + 0.3 * hash(floor(vLocal * 22.0));
  float k = (0.55 + 0.6 * f) * mottled * (1.1 - clamp(vWorld.y, 0.0, 1.2) * 0.35);
  vec3 c = tint * k;
  // Cosmetic glass patterns (bought in the shop; looks only).
  if (pattern > 0.5 && pattern < 1.5) { float f2 = abs(fract((vLocal.x + vLocal.y) * 14.0) - 0.5) + abs(fract((vLocal.x - vLocal.y) * 14.0) - 0.5); c *= 0.75 + 0.7 * step(0.62, f2); }   // 切子: cut facets
  if (pattern > 1.5 && pattern < 2.5) { vec2 q = fract(vLocal.xz * 9.0) - 0.5; c = mix(c, c * 0.35 + vec3(0.9), smoothstep(0.2, 0.12, abs(length(q) - 0.25)) * 0.6); } // 絞り: tie-dye rings
  if (pattern > 2.5) { c += vec3(1.0, 0.8, 0.3) * step(0.93, hash(floor(vLocal * 40.0))) * 0.9; }   // 金襴: gold flecks
  c = mix(c, vec3(dot(c, vec3(0.33))) * 0.3, dim);
  gl_FragColor = vec4(c + glow + flash, 1.0);
}`;
const charVS = /* glsl */`
varying vec3 vNormal; varying vec3 vView; varying vec3 vWorld; varying vec3 vLocal;
void main() {
  vLocal = position;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vView = cameraPosition - w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const eyeFS = /* glsl */`
uniform float opacity; uniform float dim; uniform vec3 eye;
void main() { if (opacity < 0.5) discard; gl_FragColor = vec4(mix(eye, vec3(0.2), dim), 1.0); }`;

function placeholderTemplates() {
  // Same node contract as the Blender export, built from primitives.
  const T = {};
  const m = (geo, name, color) => { const o = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color })); o.name = name; return o; };
  const crab = (id, shellGeo) => {
    const root = new THREE.Group(); root.name = 'Char_' + id;
    const body = m(new THREE.SphereGeometry(0.32, 12, 8).scale(1.2, 0.6, 1), 'Body_Main', 0x000000); body.position.y = 0.18;
    const legs = m(new THREE.TorusGeometry(0.38, 0.05, 4, 12).rotateX(Math.PI / 2), 'Body_Legs', 0x000000); legs.position.y = 0.06;
    const clawL = new THREE.Group(); clawL.name = 'Claw_L'; clawL.position.set(-0.28, 0.2, 0.3); clawL.add(m(new THREE.SphereGeometry(0.11, 8, 6).scale(1, 0.7, 1.3), 'Body_ClawL', 0));
    const clawR = new THREE.Group(); clawR.name = 'Claw_R'; clawR.position.set(0.3, 0.2, 0.3); clawR.add(m(new THREE.SphereGeometry(0.16, 8, 6).scale(1, 0.7, 1.3), 'Body_ClawR', 0));
    const shell = new THREE.Group(); shell.name = 'Shell'; shell.position.set(0, 0.42, -0.08);
    shell.add(m(shellGeo, 'Glass_Shell', CRAB_TINTS[['goro', 'bou', 'chika', 'mio'].indexOf(id)].getHex()));
    const eyes = ['L', 'R'].map((s, k) => {
      const g = new THREE.Group(); g.name = 'EyeStalk_' + s; g.position.set(k ? 0.1 : -0.1, 0.25, 0.25);
      g.add(m(new THREE.CylinderGeometry(0.02, 0.025, 0.22, 5).translate(0, 0.11, 0), 'Body_Stalk' + s, 0));
      const e = m(new THREE.SphereGeometry(0.045, 6, 4), 'Eye_' + s, 0xffffff); e.position.y = 0.24; g.add(e);
      return g;
    });
    root.add(body, legs, clawL, clawR, shell, ...eyes);
    T[id] = root;
  };
  crab('goro', new THREE.IcosahedronGeometry(0.42, 0).scale(1, 0.85, 1));
  crab('bou', new THREE.ConeGeometry(0.34, 0.95, 9).rotateX(-0.9));
  crab('chika', new THREE.SphereGeometry(0.4, 12, 8).scale(1, 0.7, 1.25));
  crab('mio', new THREE.SphereGeometry(0.42, 14, 10).scale(1, 0.9, 1));
  const little = (id, color, geo) => {
    const root = new THREE.Group(); root.name = 'Char_' + id;
    const g = m(geo, 'Glass_Body', color); g.position.y = 0.2;
    const outline = m(geo.clone().scale(1.12, 1.12, 1.12), 'Body_Back', 0); outline.position.set(0, 0.2, -0.03);
    const e = m(new THREE.SphereGeometry(0.035, 6, 4), 'Eye_A', 0xffffff); e.position.set(0.08, 0.26, 0.15);
    root.add(outline, g, e); T[id] = root;
  };
  little('fry', 0xc9e4ff, new THREE.SphereGeometry(0.2, 10, 6).scale(1.6, 0.6, 0.7));
  little('takoko', 0xff8fb8, new THREE.SphereGeometry(0.18, 10, 8).scale(1, 1.1, 1));
  little('seahorse', 0xffd84a, new THREE.CapsuleGeometry(0.08, 0.3, 4, 8));
  little('fugu', 0xf3e2a0, new THREE.IcosahedronGeometry(0.2, 1));
  const an = new THREE.Group(); an.name = 'Char_anemone';
  an.add(m(new THREE.CylinderGeometry(0.35, 0.45, 0.25, 10).translate(0, 0.12, 0), 'Body_Base', 0));
  for (let k = 0; k < 10; k++) { const t = m(new THREE.ConeGeometry(0.06, 0.4, 5).translate(0, 0.2, 0), 'Glass_Tentacle' + k, 0x5fe0c8); t.position.set(Math.cos(k) * 0.25, 0.25, Math.sin(k) * 0.25); t.rotation.set(Math.sin(k) * 0.5, 0, Math.cos(k) * 0.5); an.add(t); }
  T.anemone = an;
  const nu = new THREE.Group(); nu.name = 'Char_nushi';
  nu.add(m(new THREE.SphereGeometry(1.1, 16, 12).scale(1, 0.9, 1).translate(0, 0.6, 0), 'Body_Head', 0));
  for (let k = 0; k < 8; k++) { const a = m(new THREE.TorusGeometry(0.7, 0.13, 5, 10, Math.PI).rotateY(k / 8 * Math.PI * 2), 'Body_Arm' + k, 0); a.position.set(Math.cos(k / 8 * 6.28) * 1.1, 0.05, Math.sin(k / 8 * 6.28) * 1.1); nu.add(a); }
  for (const s of [-1, 1]) { const e = m(new THREE.SphereGeometry(0.16, 8, 6), 'Glass_Eye', 0xffd36a); e.position.set(s * 0.4, 0.95, 0.85); nu.add(e); }
  T.nushi = nu;
  return T;
}

export class CharacterBank {
  async load(url = '/models/tsukinoura-characters.glb') {
    this.templates = placeholderTemplates();
    try {
      const gltf = await new GLTFLoader().loadAsync(url);
      for (const c of gltf.scene.children) if (c.name.startsWith('Char_')) this.templates[c.name.slice(5)] = c;
      this.real = true;
    } catch (e) { console.info('characters: using placeholders', e.message); }
  }
  actor(name, opts = {}) { return new Character3D(this.templates[name], name, opts); }
}

const CRAB_ANIMS = { idle: 1, pull: 1, spin: 1, land: 1, cheer: 1, stuck: 1, walk: 1 };
const LITTLE_ANIMS = { idle: 1, bump: 1, saved: 1, taken: 1 };

const PATTERNS = { kiriko: 1, shibori: 2, kinran: 3 };
// Which holes each shell's card has: see SHELL_HOLES in sim/shells.js.

// Radial light for the pool under a puppet: bright core, soft edge.
let HALO = null;
function haloTexture() {
  if (HALO) return HALO;
  const cv = document.createElement('canvas'); cv.width = cv.height = 128;
  const ctx = cv.getContext('2d');
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, 'rgba(255,255,255,0.95)'); g.addColorStop(0.45, 'rgba(255,255,255,0.55)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
  HALO = new THREE.CanvasTexture(cv); HALO.colorSpace = THREE.SRGBColorSpace;
  return HALO;
}
const firstGlassColor = model => {
  let c = null;
  model.traverse(o => { if (!c && o.isMesh && /^Glass_/.test(o.userData?.name || o.name)) c = (m => m.uniforms?.tint?.value?.clone() || m.color?.clone())(Array.isArray(o.material) ? o.material[0] : o.material); });
  return c || new THREE.Color(0.9, 0.9, 1.0);
};

export class Character3D {
  constructor(template, kind, { shell = null, pattern = '' } = {}) {
    this.kind = kind;
    this.crab = ['goro', 'bou', 'chika', 'mio'].includes(kind);
    this.anims = this.crab ? CRAB_ANIMS : kind === 'anemone' ? { idle: 1, hug: 1 } : kind === 'nushi' ? { idle: 1, ink: 1, calm: 1 } : { ...LITTLE_ANIMS, ...(kind === 'fugu' ? { puff: 1 } : {}) };
    this.uniforms = {
      glow: { value: new THREE.Color(0, 0, 0) }, opacity: { value: 1 }, dim: { value: 0 }, flash: { value: 0 },
      rim: { value: KAGEE.rimLight }, time: { value: 0 }, light: { value: new THREE.Color(1, 1, 1) },
      pattern: { value: PATTERNS[pattern] || 0 }, eye: { value: new THREE.Color(0.75, 0.7, 0.55) },
    };
    const holes = this.crab ? (HOLES[shell || DEFAULT_SHELLS[kind]] ?? 0) : -1;
    // Wearing another shell (宿替え): the shell glass takes that shell's colour.
    this.shellTint = shell && SHELL_GLASS[shell] ? new THREE.Color(SHELL_GLASS[shell]) : null;
    this.model = template.clone(true);
    this.model.position.set(0, 0, 0);
    this.model.traverse(o => {
      if (!o.isMesh) return;
      const name = o.userData?.name || o.name;
      const base = (Array.isArray(o.material) ? o.material[0] : o.material);
      const u = this.uniforms;
      if (/^Eye_/.test(name)) o.material = new THREE.ShaderMaterial({ vertexShader: charVS, fragmentShader: eyeFS, uniforms: u });
      else if (/^Glass_/.test(name)) {
        const inShell = this.crab && /Shell/.test(name);
        const src = inShell && this.shellTint && /^Glass_Shell$|^Glass_Shell_/.test(name) ? this.shellTint : base.color;
        const tint = (src ? src.clone() : new THREE.Color(1, 1, 1)).multiplyScalar(0.8);
        o.material = new THREE.ShaderMaterial({ vertexShader: charVS, fragmentShader: glassFS, uniforms: { ...u, tint: { value: tint }, holes: { value: inShell ? holes : -1 } } });
      } else o.material = new THREE.ShaderMaterial({ vertexShader: charVS, fragmentShader: bodyFS, uniforms: u });
      o.renderOrder = 1;
    });
    // GLTFLoader renames repeated node names (Shell_1, …); the exported name lives in userData.name.
    const find = n => { let r = null; this.model.traverse(o => { if (!r && (o.userData?.name === n || o.name === n)) r = o; }); return r; };
    this.parts = {
      shell: find('Shell'), crabBody: find('Crab'), clawL: find('Claw_L'), clawR: find('Claw_R'),
      eyeL: find('EyeStalk_L'), eyeR: find('EyeStalk_R'), spines: find('Body_Spines'),
    };
    this.rest = {};
    for (const [k, p] of Object.entries(this.parts)) if (p) this.rest[k] = { pos: p.position.clone(), rot: p.rotation.clone(), scale: p.scale.clone() };
    this.body = []; this.model.traverse(o => { if (o.isMesh && /^Body_|^Eye_|^Lead_/.test(o.name) && !this.isInShell(o)) this.body.push(o); });
    // A puppet needs light behind it: crabs and little ones stand in a soft
    // pool of their own colour (the lamp behind the screen); the big ones keep a shadow.
    this.backlit = this.crab || !['nushi', 'anemone'].includes(kind);
    const haloColor = this.crab ? (this.shellTint || CRAB_TINTS[['goro', 'bou', 'chika', 'mio'].indexOf(kind)]).clone() : firstGlassColor(this.model);
    this.shadow = new THREE.Mesh(new THREE.CircleGeometry(0.5, 32), this.backlit
      ? new THREE.MeshBasicMaterial({ map: haloTexture(), color: haloColor.lerp(new THREE.Color(1, 0.95, 0.85), 0.35).multiplyScalar(0.55), transparent: true, depthWrite: false })
      : new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.45, depthWrite: false }));
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.renderOrder = 3;
    this.holder = new THREE.Group();
    this.holder.add(this.model);
    this.group = new THREE.Group();
    this.group.add(this.holder, this.shadow);
    this.mesh = this.holder;   // view.js reads .mesh.position for small offsets
    this.scale = new THREE.Vector2(1, 1);
    // A little larger than the physics body, so silhouettes read at play distance.
    this.size = kind === 'nushi' ? 1 : this.crab ? 1.3 : kind === 'anemone' ? 1 : 1.7;
    // Lean the figure toward the camera, like a shadow puppet held up to the screen,
    // so its front outline (claws, eye stalks, legs) shows from the 55° view.
    this.lean = kind === 'nushi' ? -0.3 : kind === 'anemone' ? -0.15 : -0.55;
    this.t = 0; this.yaw = 0; this.yawGoal = 0;
    this.floorY = -0.3;
    this.shadowSize = kind === 'nushi' ? 3.2 : this.crab ? 1.7 : kind === 'anemone' ? 0.6 : 1.1;
    this.play('idle');
  }

  isInShell(o) { let p = o; while (p) { if (p.name === 'Shell') return true; p = p.parent; } return false; }
  has(name) { return !!this.anims[name]; }
  play(name, { restart = false, then = null } = {}) {
    if (!this.has(name)) return;
    if (this.anim === name && !restart) return;
    this.anim = name; this.t = 0; this.then = then; this.holding = false;
  }
  hold(name, k) { this.anim = name; this.holdFrame = k; this.holding = true; }
  release() { this.holding = false; }
  set flip(v) { if (this.crab) this.yawGoal = v ? -0.45 : 0.45; }

  // Procedural animation in place of sprite frames.
  update(dt, camera) {
    this.t += dt;
    const t = this.t, P = this.parts, R = this.rest;
    const reset = k => { const p = P[k]; if (p && R[k]) { p.position.copy(R[k].pos); p.rotation.copy(R[k].rot); p.scale.copy(R[k].scale); } };
    this.holder.rotation.z = 0;
    for (const k of Object.keys(P)) reset(k);
    let bodyShow = 1, hop = 0, sx = 1, sy = 1;
    const a = this.anim;
    if (this.crab) {
      if (a === 'idle') {
        const s = Math.sin(t * 2.2);
        if (P.eyeL) { P.eyeL.rotation.z = 0.12 * s; P.eyeR.rotation.z = -0.12 * Math.sin(t * 2.2 + 0.7); }
        if (P.clawR) P.clawR.rotation.x = Math.max(0, Math.sin(t * 1.3)) * -0.35;
        hop = Math.abs(Math.sin(t * 1.1)) * 0.015;
      } else if (a === 'pull') {
        const k = this.holding ? (this.holdFrame + 1) / 3 : Math.min(1, t * 4);
        bodyShow = 1 - k * 0.92;
        if (P.shell) P.shell.position.y -= k * 0.14;
      } else if (a === 'spin') {
        bodyShow = 0.05;
        if (P.shell) { P.shell.rotation.y = t * 26; P.shell.position.y -= 0.12; }
      } else if (a === 'land') {
        const k = Math.min(1, t / 0.35);
        bodyShow = k < 0.6 ? k / 0.6 * 1.15 : 1.15 - (k - 0.6) / 0.4 * 0.15;
        if (P.shell) P.shell.rotation.z = Math.sin(t * 30) * 0.18 * (1 - k);
        if (k >= 1 && this.then) { const n = this.then; this.then = null; this.play(n); }
      } else if (a === 'cheer') {
        hop = Math.abs(Math.sin(t * 9)) * 0.18;
        if (P.clawL) { P.clawL.rotation.x = -1.1; P.clawR.rotation.x = -1.1; P.clawL.position.y += 0.15; P.clawR.position.y += 0.15; }
        if (t > 1.1 && this.then) { const n = this.then; this.then = null; this.play(n); }
      } else if (a === 'stuck') {
        if (P.clawL) { P.clawL.rotation.x = Math.sin(t * 24) * 0.6; P.clawR.rotation.x = Math.cos(t * 24) * 0.6; }
        this.holder.rotation.z = Math.sin(t * 18) * 0.05;
      } else if (a === 'walk') {
        hop = Math.abs(Math.sin(t * 14)) * 0.05;
        this.holder.rotation.z = Math.sin(t * 14) * 0.06;
      }
      // The crab pulls itself into its shell: shrink the body toward the shell's centre.
      const bodyScale = Math.max(0.05, bodyShow);
      if (P.crabBody && P.shell) {
        P.crabBody.visible = bodyShow > 0.08;
        const sc = R.shell.pos;
        P.crabBody.scale.setScalar(bodyScale);
        P.crabBody.position.set(sc.x * (1 - bodyScale), sc.y * (1 - bodyScale) * 0.8, sc.z * (1 - bodyScale));
      } else {
        for (const o of this.body) o.visible = bodyShow > 0.08;
        if (P.clawL) { P.clawL.scale.multiplyScalar(bodyScale); P.clawR.scale.multiplyScalar(bodyScale); }
        if (P.eyeL) { P.eyeL.scale.multiplyScalar(bodyScale); P.eyeR.scale.multiplyScalar(bodyScale); }
      }
    } else if (this.kind === 'nushi') {
      sx = sy = 1 + Math.sin(t * 1.4) * 0.03;
      if (a === 'ink') this.uniforms.flash.value = 0;
      if (a === 'calm') { this.model.position.y = -Math.min(0.5, t * 0.15); this.uniforms.dim.value = Math.min(0.5, t * 0.2); }
    } else if (this.kind === 'anemone') {
      this.model.children.forEach((c, i) => { if (/Glass/.test(c.name)) c.rotation.y = Math.sin(t * 2 + i) * 0.15; });
      if (a === 'hug') { sx = 0.85; sy = 1.1; if (t > 0.5 && this.then) { const n = this.then; this.then = null; this.play(n); } }
    } else {
      hop = 0.06 + Math.sin(t * 3 + this.kind.length) * 0.04;
      this.holder.rotation.y = Math.sin(t * 1.3) * 0.4;
      if (a === 'bump') { sx = 1.25; sy = 0.75; if (t > 0.25 && this.then) { const n = this.then; this.then = null; this.play(n); } }
      if (a === 'puff') {
        const k = Math.min(1, t * 4);
        sx = sy = 1 + 0.35 * k;
        if (P.spines) P.spines.scale.setScalar(1 + 0.6 * k);
        if (t > 0.7 && this.then) { const n = this.then; this.then = null; this.play(n); }
      }
      if (a === 'saved') hop += t * 0.3;
    }
    this.yaw += (this.yawGoal - this.yaw) * Math.min(1, dt * 8);
    if (this.crab) this.holder.rotation.y = this.yaw;
    this.holder.rotation.x = this.lean;
    // Face the camera's side of the pool (the portrait camera may be turned a quarter).
    if (camera) this.group.rotation.y = Math.round(Math.atan2(camera.position.x - this.group.position.x, camera.position.z - this.group.position.z) / (Math.PI / 2)) * (Math.PI / 2);
    const k = this.size ?? 1;
    this.holder.scale.set(this.scale.x * sx * k, this.scale.y * sy * k, this.scale.x * sx * k);
    this.holder.position.y = hop;
    this.holder.position.x = this.offsetX || 0;
    this.uniforms.time.value += dt;
    this.shadow.position.set(0, 0.012 - this.group.position.y, 0.08);
    const ss = this.shadowSize * (1 - Math.min(0.5, hop * 2));
    this.shadow.scale.set(ss, this.backlit ? ss : ss * 0.8, 1);
    this.shadow.material.opacity = (this.backlit ? 0.9 * (1 - this.uniforms.dim.value * 0.7) : 0.14) * this.uniforms.opacity.value;
  }

  dispose() {
    this.group.traverse(o => { if (o.isMesh) { o.material.dispose(); } });
    this.shadow.geometry.dispose();
  }
}

// ------------------------------------------------------------------ foreground frame

// Black paper cut-outs held close to the lens, like the front layer of a
// shadow-play stage: a pine bough with a hanging glass float top-right, beach
// grass bottom-left. Drawn procedurally, camera-locked, blurred by the DOF.
function drawBough(w, h, seed) {
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d');
  let s = seed;
  const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  ctx.fillStyle = '#000'; ctx.strokeStyle = '#000'; ctx.lineCap = 'round';
  const branch = (x, y, a, len, wdt, depth) => {
    const x2 = x + Math.cos(a) * len, y2 = y + Math.sin(a) * len;
    ctx.lineWidth = wdt; ctx.beginPath(); ctx.moveTo(x, y);
    ctx.quadraticCurveTo((x + x2) / 2 + (rnd() - 0.5) * len * 0.3, (y + y2) / 2 + (rnd() - 0.5) * len * 0.3, x2, y2); ctx.stroke();
    if (depth <= 0) {
      // A flat pad of needles, the kuromatsu silhouette.
      ctx.beginPath(); ctx.ellipse(x2, y2, len * 0.9, len * 0.32, a * 0.2, 0, Math.PI * 2); ctx.fill();
      for (let k = 0; k < 26; k++) { const t = rnd() * Math.PI * 2, r = len * (0.7 + rnd() * 0.5); ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(x2 + Math.cos(t) * r, y2 + Math.sin(t) * r * 0.45); ctx.stroke(); }
      return;
    }
    for (let k = 0; k < 2; k++) branch(x2, y2, a + (rnd() - 0.5) * 1.3, len * (0.62 + rnd() * 0.2), wdt * 0.62, depth - 1);
  };
  branch(w * 1.02, h * 0.08, Math.PI * 0.9, w * 0.28, 26, 3);
  // A hanging cord.
  ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(w * 0.62, h * 0.18); ctx.lineTo(w * 0.6, h * 0.55); ctx.stroke();
  return cv;
}
function drawGrass(w, h, seed) {
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d');
  let s = seed;
  const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  ctx.fillStyle = '#000';
  for (let k = 0; k < 34; k++) {
    const x = rnd() * w * 0.75, top = h * (0.25 + rnd() * 0.5), lean = (rnd() - 0.3) * w * 0.25, wd = 3 + rnd() * 6;
    ctx.beginPath(); ctx.moveTo(x - wd, h); ctx.quadraticCurveTo(x + lean * 0.3, (h + top) / 2, x + lean, top); ctx.quadraticCurveTo(x + lean * 0.3 + wd * 0.4, (h + top) / 2, x + wd, h); ctx.fill();
  }
  ctx.fillRect(0, h * 0.9, w * 0.8, h * 0.1);
  return cv;
}

export function addForegroundFrame(stage) {
  const group = new THREE.Group();
  const plane = (canvas, wUnits, hUnits) => {
    const tex = new THREE.CanvasTexture(canvas);
    const m = new THREE.Mesh(new THREE.PlaneGeometry(wUnits, hUnits), new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.4, color: 0x000000 }));
    m.renderOrder = 9;
    return m;
  };
  const bough = plane(drawBough(512, 384, 7), 4.2, 3.15);
  const grass = plane(drawGrass(512, 256, 11), 3.6, 1.8);
  // A glass fishing float hanging from the bough, black paper like the rest of the frame.
  const float = new THREE.Mesh(new THREE.SphereGeometry(0.17, 16, 12), new THREE.MeshBasicMaterial({ color: 0x050408 }));
  const net = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.012, 4, 16), new THREE.MeshBasicMaterial({ color: 0x000000 }));
  group.add(bough, grass, float, net);
  stage.camera.add(group);
  stage.scene.add(stage.camera);
  stage.frame = { group, bough, grass, float, net };
  layoutForeground(stage);
  addEventListener('resize', () => layoutForeground(stage));
}

// Keep the cut-outs in the corners at a fixed distance in front of the lens.
export function layoutForeground(stage) {
  const f = stage.frame; if (!f) return;
  const d = 7, half = Math.tan(THREE.MathUtils.degToRad(stage.camera.fov / 2)) * d, aspect = stage.aspect || 1.6;
  const portrait = aspect < 0.8;
  // Sizes as fractions of the visible height. The bough leans in from the right
  // edge (the HUD owns the corners); grass rises at the bottom-right.
  const bh = half * 2 * (portrait ? 0.22 : 0.36), gh = half * 2 * (portrait ? 0.1 : 0.15);
  f.bough.scale.set(bh / 3.15, bh / 3.15, 1);
  f.grass.scale.set(-gh / 1.8, gh / 1.8, 1);
  f.bough.position.set(half * aspect - (4.2 * f.bough.scale.x) / 2 + 0.06 * half, half * (portrait ? 0.35 : 0.22), -d);
  f.grass.position.set(half * aspect - (3.6 * gh / 1.8) / 2 + 0.04 * half, -half + gh / 2 - 0.02 * half, -d);
  const fs = f.bough.scale.x;
  f.float.scale.setScalar(fs * 0.9); f.net.scale.setScalar(fs * 0.9);
  f.float.position.set(f.bough.position.x - 0.4 * 4.2 * fs * 0.5 + 0.35 * fs, f.bough.position.y - 0.62 * fs, -d + 0.05);
  f.net.position.copy(f.float.position);
}
